import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import ScrollableTable, {
  type ITableHeader,
} from "../../../../../../components/shared/ScrollableTable/ScrollableTable";
import { usePostApi } from "../../../../../../hooks";
import { type actionType, type ITankPlan } from "../tankPlans.model";
import TankPlansTableRow from "./TankPlansTableRow";
import styles from "./TankPlansTableRow.module.scss";

interface Props {
  data: ITankPlan[];
  onAction: (action: actionType, rowIdx: number) => void;
  refetch: () => void;
  onSortedDataChange?: (sortedData: ITankPlan[]) => void;
  syncOrder?: boolean;
}

const headers: ITableHeader[] = [
  { label: "Type", name: "type", minWidth: 80, className: styles.headerCell },
  { label: "Name", name: "name", minWidth: 200, className: styles.headerCell },
  {
    label: "Capacity",
    name: "capacity",
    minWidth: 80,
    className: styles.headerCell,
  },
  {
    label: "Oil Record Book",
    name: "orb",
    minWidth: 150,
    className: styles.headerCell,
  },
  {
    label: "Ballast Water Log",
    name: "bwl",
    minWidth: 150,
    className: styles.headerCell,
  },
  {
    label: "Marine Fuel Sulphur Part A",
    name: "mfsbA",
    minWidth: 220,
    className: styles.headerCell,
  },
  {
    label: "Marine Fuel Sulphur Part B",
    name: "mfsbB",
    minWidth: 220,
    className: styles.headerCell,
  },
  {
    label: "",
    name: "actions",
    sortable: false,
    className: `${styles.headerCell} ${styles.actionsHeader}`,
    style: { width: "1%", whiteSpace: "nowrap" },
  },
];

const TankPlansTable = ({
  data,
  onAction,
  refetch,
  onSortedDataChange,
  syncOrder = true,
}: Props) => {
  const { post } = usePostApi();
  const [searchParams] = useSearchParams();

  const sortBy = searchParams.get("sort");
  const sortDir = searchParams.get("dir") === "desc" ? "desc" : "asc";

  const sortedData = useMemo(() => {
    if (!sortBy || sortBy === "actions") return data;

    return [...data].sort((a, b) => {
      const sanitize = (val: string | undefined) =>
        parseFloat((val ?? "0").replace(/[^0-9.]/g, ""));

      const aVal =
        sortBy === "capacity"
          ? sanitize(a.capacity?.value)
          : sortBy === "orb"
            ? a.orb?.[0]
            : (a as any)[sortBy];

      const bVal =
        sortBy === "capacity"
          ? sanitize(b.capacity?.value)
          : sortBy === "orb"
            ? b.orb?.[0]
            : (b as any)[sortBy];

      if (aVal === undefined || bVal === undefined) return 0;

      if (typeof aVal === "boolean" && typeof bVal === "boolean") {
        return sortDir === "asc"
          ? Number(aVal) - Number(bVal)
          : Number(bVal) - Number(aVal);
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, sortBy, sortDir]);

  useEffect(() => {
    onSortedDataChange?.(sortedData);
  }, [sortedData, onSortedDataChange]);

  useEffect(() => {
    if (!syncOrder) return;

    const list = sortedData.map(({ tankid }, i) => ({
      tankid,
      order: i,
    }));
    void post({
      request: "set-order-tanks",
      body: { params: { list } },
    });
  }, [sortedData, post, syncOrder]);
  const renderRow = useCallback(
    ({ row, index }: { row: ITankPlan; index?: number }) => (
      <TankPlansTableRow
        key={row.tankid}
        index={index ?? 0}
        {...row}
        onAction={onAction}
        refetch={refetch}
      />
    ),
    [onAction, refetch],
  );
  return (
    <ScrollableTable
      data={sortedData}
      headers={headers}
      renderRow={renderRow}
      loading={false}
      sortable
      keyExtractor={(row: ITankPlan) => row.tankid}
    />
  );
};

export default TankPlansTable;
